import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "../../Utills/toast";
import Tabs from "../../component/Tabs";
import ListItem from "../../component/ListItem";
import SelectItem from "../../component/MenuCard/SelectItem";
import { addNote, clearCartForTable, fetchMenuData, fetchTaxRequest } from "../../redux/action";
import { createTakeawayBill, createTakeawayOrder, getTakeawayBill, getTakeawaySettings } from "./takeawayApi";
import { ButtonLoader } from "../../component/common/Loader";
import PrintBill from "../../component/PrintBill/PrintBill";
import "./takeaway.css";
import { usePaymentMethodRequirement } from "../../Utills/usePaymentMethodRequirement";
import { calculateBillTotals } from "../../Utills/billingTaxCalculator";
import { useTrackedPrint } from "../../Utills/useTrackedPrint";

const CART_KEY = "TAKEAWAY_ORDER";

const TakeawayCreate = () => {
  const requirePaymentMethod = usePaymentMethodRequirement();
  const dispatch = useDispatch();
  const printRef = useRef(null);
  const autoPrintedOrderId = useRef(null);
  const menu = useSelector((state) => state?.user?.menu);
  const cart = useSelector((state) => state?.user?.cart?.[CART_KEY] || {});
  const taxSettings = useSelector((state) => state?.admin?.tax);
  const taxSettingsError = useSelector((state) => state?.admin?.error);
  const [categoryId, setCategoryId] = useState("");
  const [search, setSearch] = useState("");
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(null);
  const [printDetails, setPrintDetails] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [showOrderInstructions, setShowOrderInstructions] = useState(false);
  const [expandedItemNotes, setExpandedItemNotes] = useState({});
  const [form, setForm] = useState({
    customerName: "", customerPhone: "", orderSource: 4,
    fulfillmentType: 0,
    paymentMode: "", isPaid: false, printBill: false, discountAmount: "", applyServiceCharge: false,
    pickupTime: "", specialInstructions: ""
  });

  useEffect(() => {
    dispatch(fetchMenuData());
    dispatch(fetchTaxRequest());
    getTakeawaySettings().then(setSettings).catch((error) => toast.error(error.message));
  }, [dispatch]);

  useEffect(() => {
    if (!categoryId && menu?.categories?.length) setCategoryId(menu.categories[0].categoryId);
  }, [categoryId, menu]);

  useEffect(() => {
    if (!isCartOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsCartOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [isCartOpen]);

  const entries = useMemo(() => Object.entries(cart), [cart]);
  const itemCount = entries.reduce((sum, [, item]) => sum + Number(item.selected || 0), 0);
  const print = useTrackedPrint({
    contentRef: printRef,
    documentType: "BILL",
    getDocumentId: () => printDetails?.details?.bill?.billId || printDetails?.order?.takeawayOrderId
  });
  const isDineIn = form.fulfillmentType === 1;
  const discountAmount = form.isPaid ? Number(form.discountAmount || 0) : 0;
  const menuItemsById = useMemo(() => new Map(
    (menu?.categories || []).flatMap((category) => category.items || []).map((item) => [item.menuId, item])
  ), [menu]);
  const billTotals = useMemo(() => calculateBillTotals({
    items: entries.map(([, item]) => {
      const menuItem = menuItemsById.get(item.menuId);
      return {
        menuId: item.menuId,
        price: item.price,
        quantity: item.selected,
        taxCategoryId: menuItem?.taxCategoryId,
        isTaxExempt: menuItem?.isTaxExempt,
        isDiscountEligible: menuItem?.isDiscountEligible
      };
    }),
    settings: taxSettings || {},
    orderMode: isDineIn ? "DINE_IN" : "PICKUP",
    discount: discountAmount,
    applyServiceCharge: form.isPaid && form.applyServiceCharge,
    legacyPackaging: isDineIn ? 0 : Number(settings?.packagingCharge || 0)
  }), [entries, form.applyServiceCharge, form.isPaid, isDineIn, discountAmount, menuItemsById, settings, taxSettings]);
  const packagingCharge = billTotals.packagingCharge;
  const displayedTotal = billTotals.grandTotal;
  const activeCategory = menu?.categories?.find((category) => category.categoryId === categoryId);
  const filteredCategory = activeCategory ? {
    ...activeCategory,
    items: (activeCategory.items || []).filter((item) =>
      (item.foodName || item.name || "").toLowerCase().includes(search.trim().toLowerCase()))
  } : null;
  const tabs = (menu?.categories || []).map((category) => ({
    categoryId: category.categoryId,
    category: category.categoryName,
    isSelected: category.categoryId === categoryId
  }));

  useEffect(() => {
    const orderId = printDetails?.order?.takeawayOrderId;
    if (!orderId || autoPrintedOrderId.current === orderId) return;
    autoPrintedOrderId.current = orderId;
    print();
  }, [print, printDetails]);

  const update = (name, value) => setForm((current) => ({ ...current, [name]: value }));
  const toggleItemNote = (key, item) => {
    const isVisible = expandedItemNotes[key] ?? Boolean(item.note);
    if (isVisible) dispatch(addNote({ tableName: CART_KEY, id: key, note: "" }));
    setExpandedItemNotes((current) => ({ ...current, [key]: !isVisible }));
  };
  const updatePaymentReceived = (isPaid) => setForm((current) => ({
    ...current,
    isPaid,
    paymentMode: isPaid ? current.paymentMode : "",
    discountAmount: isPaid ? current.discountAmount : "",
    applyServiceCharge: isPaid ? current.applyServiceCharge : false
  }));

  const submit = async (event) => {
    event.preventDefault();
    if (!entries.length) return toast.warning("Add at least one menu item.");
    if (form.isPaid && requirePaymentMethod && !["Cash", "Card", "UPI"].includes(form.paymentMode)) {
      return toast.warning("Choose Cash, Card or UPI for a received payment.");
    }
    setLoading(true);
    try {
      const result = await createTakeawayOrder({
        fulfillmentType: form.fulfillmentType,
        orderSource: Number(form.orderSource),
        customerName: form.customerName || null,
        customerPhone: form.customerPhone || null,
        paymentMode: form.isPaid ? (form.paymentMode || "Unspecified") : (settings?.allowPayAtPickup ? "PayAtPickup" : "Cash"),
        paymentStatus: form.isPaid ? 1 : 0,
        discountAmount: form.isPaid ? Number(form.discountAmount || 0) : 0,
        applyServiceCharge: form.isPaid && form.applyServiceCharge,
        pickupTime: isDineIn ? null : (form.pickupTime || null),
        specialInstructions: form.specialInstructions || null,
        items: entries.map(([, item]) => ({
          menuId: item.menuId,
          sizeOptionId: item.selectedSize?.sizeOptionId || null,
          quantity: Number(item.selected),
          note: item.note || null,
          addonItemIds: (item.selectedAddons || []).map((addon) => addon.addonItemId)
        }))
      });
      setCreated(result);
      dispatch(clearCartForTable(CART_KEY));
      toast.success(`Takeaway ${result.tokenNumber} created.`);
      if (form.printBill) {
        try {
          let details = await getTakeawayBill(result.takeawayOrderId);
          if (!details) {
            details = await createTakeawayBill(result.takeawayOrderId, {
              paymentMode: form.isPaid ? (form.paymentMode || "Unspecified") : "Cash",
              markPaid: form.isPaid,
              applyServiceCharge: form.isPaid && form.applyServiceCharge,
              discountAmount: form.isPaid ? Number(form.discountAmount || 0) : 0,
              settlementAmount: null
            });
          }
          setPrintDetails({ order: result, details });
        } catch (error) {
          toast.error(`Order created, but the bill could not be prepared for printing: ${error.message}`);
        }
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (created) return (
    <div className="takeaway-page">
      <div className="takeaway-success">
        <span>Pickup token</span><strong>{created.tokenNumber}</strong>
        <p>Order total: ₹{Number(created.grandTotal).toFixed(2)}</p>
        <div className="takeaway-actions">
          <button onClick={() => { setCreated(null); setPrintDetails(null); setShowCustomerDetails(false); setShowOrderInstructions(false); setExpandedItemNotes({}); setForm((current) => ({ ...current, customerName: "", customerPhone: "", paymentMode: "", isPaid: false, printBill: false, discountAmount: "", applyServiceCharge: false, pickupTime: "", specialInstructions: "" })); }}>Create another</button>
          <Link to="/takeaway/orders">Open live board</Link>
        </div>
        {printDetails && <div className="takeaway-print"><PrintBill
          ref={printRef}
          OrderAndChargesDetails={{ orders: printDetails.details.items, charges: { gstNumber: printDetails.details.gstNumber || "" } }}
          tableName={printDetails.order.tokenNumber}
          subTotal={Number(printDetails.details.bill.subTotal || 0)}
          packagingCharge={Number(printDetails.details.bill.packagingCharge || 0)}
          cgstAmount={Number(printDetails.details.bill.cgstAmount || 0)}
          sgstAmount={Number(printDetails.details.bill.sgstAmount || 0)}
          serviceChargeAmount={Number(printDetails.details.bill.serviceChargeAmount || 0)}
          appliedDiscount={Number(printDetails.details.bill.discountAmount || 0)}
          grandTotal={Number(printDetails.details.bill.grandTotal || 0)}
          orderMode={printDetails.order.fulfillmentType === 1 ? "DINE-IN" : "PICK-UP"}
          billDetails={printDetails.details.bill}
          roundOff={Number(printDetails.details.roundOff || 0)}
          payableAmount={Number(printDetails.details.payableAmount || 0)}
        /></div>}
      </div>
    </div>
  );

  if (settings && (!settings.isEnabled || !settings.isAcceptingOrders)) return (
    <div className="takeaway-page">
      <div className="takeaway-empty">
        <h2>Takeaway ordering is paused</h2>
        <p>An Admin must enable Takeaway and “Accepting orders” in settings.</p>
        <Link to="/takeaway-setting">Open Takeaway settings</Link>
      </div>
    </div>
  );

  return (
    <div className="takeaway-page">
      <div className="takeaway-title"><div><h1>New Takeaway Order</h1></div><Link to="/takeaway/orders">Live orders</Link></div>
      <div className="takeaway-create-grid">
        <section className="takeaway-menu-panel">
          <input className="takeaway-search" placeholder="Search menu item" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Tabs tabs={tabs} tabSelect={setCategoryId} />
          <ListItem category={filteredCategory} ActionComponent={SelectItem} tableName={CART_KEY} respectSchedule />
        </section>

        <div
          className={`takeaway-cart-backdrop${isCartOpen ? " is-open" : ""}`}
          aria-hidden="true"
          onClick={() => setIsCartOpen(false)}
        />
        <form className={`takeaway-cart-panel${isCartOpen ? " is-open" : ""}`} onSubmit={submit}>
          <div className="takeaway-cart-heading">
            <div><h2>Order details</h2><span>{itemCount} {itemCount === 1 ? "item" : "items"} selected</span></div>
            <button type="button" className="takeaway-cart-close" aria-label="Close order details" onClick={() => setIsCartOpen(false)}>×</button>
          </div>
          <div className="takeaway-optional-fields">
            <button
              type="button"
              className="takeaway-optional-toggle"
              aria-expanded={showCustomerDetails}
              aria-controls="takeaway-customer-details"
              onClick={() => {
                if (showCustomerDetails) update("customerName", "");
                if (showCustomerDetails) update("customerPhone", "");
                setShowCustomerDetails((visible) => !visible);
              }}
            >
              {showCustomerDetails ? "Remove customer details" : "Add customer details"}
            </button>
            {showCustomerDetails && <div className="takeaway-field-row" id="takeaway-customer-details">
              <label>Customer name (optional)<input maxLength="120" value={form.customerName} onChange={(e) => update("customerName", e.target.value)} /></label>
              <label>Phone (optional)<input type="tel" maxLength="25" value={form.customerPhone} onChange={(e) => update("customerPhone", e.target.value)} /></label>
            </div>}
          </div>
          <div className="takeaway-fulfillment-toggle" role="group" aria-label="Order type">
            <button type="button" aria-pressed={isDineIn} className={isDineIn ? "active" : ""} onClick={() => setForm((current) => ({ ...current, fulfillmentType: 1, pickupTime: "" }))}>Dine-in</button>
            <button type="button" aria-pressed={!isDineIn} className={!isDineIn ? "active" : ""} onClick={() => update("fulfillmentType", 0)}>Pick-up</button>
          </div>
          {!isDineIn && settings?.allowScheduledPickup && <label>Pickup time<input type="datetime-local" value={form.pickupTime} onChange={(e) => update("pickupTime", e.target.value)} /></label>}

          <div className="takeaway-cart-lines">
            {!entries.length && <p className="takeaway-muted">Select items from the menu.</p>}
            {entries.map(([key, item]) => {
              const showItemNote = expandedItemNotes[key] ?? Boolean(item.note);
              return <div className="takeaway-cart-line" key={key}>
                <div><strong>{item.name}</strong>{item.selectedSize?.sizeName && <small>{item.selectedSize.sizeName}</small>}{item.selectedAddons?.length > 0 && <small>{item.selectedAddons.map((addon) => addon.itemName).join(", ")}</small>}</div>
                <span>{item.selected} × ₹{Number(item.price).toFixed(2)}</span>
                <button type="button" className="takeaway-item-note-toggle" aria-expanded={showItemNote} onClick={() => toggleItemNote(key, item)}>
                  {showItemNote ? "Remove Item Note" : "+ Add Item Note"}
                </button>
                {showItemNote && <input aria-label={`Note for ${item.name}`} placeholder="Item note" maxLength="1000" value={item.note || ""} onChange={(e) => dispatch(addNote({ tableName: CART_KEY, id: key, note: e.target.value }))} />}
              </div>;
            })}
          </div>

          <div className="takeaway-optional-fields">
            <button
              type="button"
              className="takeaway-optional-toggle"
              aria-expanded={showOrderInstructions}
              aria-controls="takeaway-order-instructions"
              onClick={() => {
                if (showOrderInstructions) update("specialInstructions", "");
                setShowOrderInstructions((visible) => !visible);
              }}
            >
              {showOrderInstructions ? "Remove Order instructions" : "Add Order instructions"}
            </button>
            {showOrderInstructions && <label id="takeaway-order-instructions">Order instructions<textarea maxLength="1000" rows="2" value={form.specialInstructions} onChange={(e) => update("specialInstructions", e.target.value)} /></label>}
          </div>
          <div className="takeaway-payment-control">
            <span className="takeaway-payment-label" id="takeaway-payment-received-label">Payment received</span>
            <div className="takeaway-payment-choice">
              <button type="button" className="takeaway-payment-switch" role="switch" aria-labelledby="takeaway-payment-received-label" aria-checked={form.isPaid} onClick={() => updatePaymentReceived(!form.isPaid)}>
                <span className="takeaway-setting-toggle-track" aria-hidden="true"><span className="takeaway-setting-toggle-thumb" /></span>
              </button>
              <small>{form.isPaid ? "Yes" : "No"}</small>
            </div>
          </div>
          <div className="takeaway-payment-control">
            <span className="takeaway-payment-label" id="takeaway-print-bill-label">Print bill</span>
            <div className="takeaway-payment-choice">
              <button type="button" className="takeaway-payment-switch" role="switch" aria-labelledby="takeaway-print-bill-label" aria-checked={form.printBill} onClick={() => update("printBill", !form.printBill)}>
                <span className="takeaway-setting-toggle-track" aria-hidden="true"><span className="takeaway-setting-toggle-thumb" /></span>
              </button>
              <small>{form.printBill ? "Yes" : "No"}</small>
            </div>
          </div>
          {form.isPaid && <div className="takeaway-payment-details">
            <label>Payment mode{requirePaymentMethod ? "" : " (optional)"}<select value={form.paymentMode} onChange={(e) => update("paymentMode", e.target.value)} required={requirePaymentMethod}><option value="">Select payment mode</option><option>Cash</option><option>Card</option><option>UPI</option></select></label>
            <label>Discount amount<input type="number" min="0" step="0.01" value={form.discountAmount} onChange={(e) => update("discountAmount", e.target.value)} /></label>
            <div className="takeaway-payment-control">
              <span className="takeaway-payment-label" id="takeaway-service-charge-label">Apply service charge</span>
              <div className="takeaway-payment-choice">
                <button type="button" className="takeaway-payment-switch" role="switch" aria-labelledby="takeaway-service-charge-label" aria-checked={form.applyServiceCharge} onClick={() => update("applyServiceCharge", !form.applyServiceCharge)}>
                  <span className="takeaway-setting-toggle-track" aria-hidden="true"><span className="takeaway-setting-toggle-thumb" /></span>
                </button>
                <small>{form.applyServiceCharge ? "Yes" : "No"}</small>
              </div>
            </div>
          </div>}
          <div className="takeaway-total">
            <span>Order type</span><strong>{isDineIn ? "Dine-in" : "Pick-up"}</strong>
            <span>Subtotal</span><strong>₹{billTotals.subtotal.toFixed(2)}</strong>
            {billTotals.serviceChargeAmount > 0 && <><span>Service charge ({billTotals.serviceChargePercent}%)</span><strong>₹{billTotals.serviceChargeAmount.toFixed(2)}</strong></>}
            {billTotals.cgstAmount > 0 && <><span>CGST</span><strong>₹{billTotals.cgstAmount.toFixed(2)}</strong></>}
            {billTotals.sgstAmount > 0 && <><span>SGST</span><strong>₹{billTotals.sgstAmount.toFixed(2)}</strong></>}
            {discountAmount > 0 && <><span>Discount</span><strong>-₹{discountAmount.toFixed(2)}</strong></>}
            {packagingCharge > 0 && <><span>Packaging</span><strong>₹{packagingCharge.toFixed(2)}</strong></>}
            <span>Grand total</span><strong>₹{displayedTotal.toFixed(2)}</strong>
            {billTotals.roundOff !== 0 && <><span>Round off</span><strong>₹{billTotals.roundOff.toFixed(2)}</strong><span>Payable amount</span><strong>₹{billTotals.payableAmount.toFixed(2)}</strong></>}
          </div>
          {!taxSettings && taxSettingsError && <small className="takeaway-muted" role="status">Tax settings could not be loaded; the total may exclude configured taxes.</small>}
          <button className="takeaway-primary" disabled={loading || !entries.length} aria-busy={loading}>{loading ? <ButtonLoader label="Creating..." /> : "Create order"}</button>
        </form>
      </div>
      <div className="takeaway-mobile-cart-bar">
        <div><strong>{itemCount} {itemCount === 1 ? "item" : "items"}</strong><span>₹{billTotals.payableAmount.toFixed(2)}</span></div>
        <button type="button" onClick={() => setIsCartOpen(true)}>View order</button>
      </div>
    </div>
  );
};

export default TakeawayCreate;
